import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FlagIcon from "@mui/icons-material/Flag";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import GroupIcon from "@mui/icons-material/Group";
import { Space3DURL } from "../../utility/initApp.js";
import LayersIcon from "@mui/icons-material/Layers";
import AssigneePopup from "../AssigneePopup";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Service from "../../api/service";
import { showSuccessToast, showErrorToast } from "../toasts/ToastNotification";
import DeliverablesDots from "../DeliverablesDots";
import {
  openGlobalDropdown,
  closeGlobalDropdown,
  isGlobalDropdownOpen,
  subscribeToDropdownChanges,
} from "../../utility/dropdownState";

// ─── constants ───────────────────────────────────────────────────────────────
// Change LEFT to shift all three rows left/right together.
const LEFT = 20;
const RIGHT_PAD = 28;  // match left padding on the right for symmetry
const ICON_SIZE = 14;
const ROW1_Y = 12;   // title row top
const ROW2_Y = 36;   // subtitle row top
const ROW3_Y = 56;   // bottom row top (due date / assignee / maturity)
// ─────────────────────────────────────────────────────────────────────────────

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  const first = parts[0]?.charAt(0).toUpperCase() || "";
  const last =
    parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : "";
  return first + last;
};

const calculateTooltipSize = (text, fontSize = 12, padding = 10) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = `${fontSize}px Arial`;
  const lines = text.split("\n");
  const maxWidth = Math.max(...lines.map((line) => context.measureText(line).width));
  const height = lines.length * (fontSize + 4);
  return { width: maxWidth + padding * 2, height: height + padding * 2 };
};



const EllipseNode = ({
  id,
  x,
  y,
  width = 200,
  height = 90,
  title = "",
  subtitle = "",
  nodeColor,
  delayDays,
  startDate,
  dueDate,
  assignees: d = [],
  maturity = "",
  maturityDescription = "",
  status = "",
  statusDescription = "",
  containerRef,
  description = "",
  dragNode,
  hasChildren = false,
  isExpanded = false,
  onDoubleClick = null,
  onMouseDown,
  isInitial = false,
  hierarchyDepth = 0,
  objectId,
  totalFloat = 0,
  showCriticalPath = false,
  isInChain = false,
  onHighlightChain = null,
  opacity = 1,
  loading = false,
  forceResetHover = false,
  projectId,
  onAddDeliverables = null,
  onRefresh,
  onUpdateNode,
  onPromoteNode,
  timelineMode,
  zoomRatio,
}) => {
  const safeStatus = (status || "").toLowerCase().trim();
  const showWarningIcon =
    safeStatus === "late" || safeStatus === "delay" || safeStatus === "delayed" || delayDays > 0;

  const nodeRef = useRef(null);
  const dropdownRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    textNode: null,
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });

  const ENOVIA_BASE_URL = Space3DURL;

  const showLocalTooltip = (tooltipString, tooltipNode) => {
    const { width: tw, height: th } = calculateTooltipSize(tooltipString);
    setTooltip({ visible: true, text: tooltipString, textNode: tooltipNode, width: tw, height: th, x: 0, y: 0 });
  };

  const hideLocalTooltip = () => setTooltip((t) => ({ ...t, visible: false }));

  const [highlighted, setHighlighted] = useState(false);
  const [isThisDropdownOpen, setIsThisDropdownOpen] = useState(false);
  const [showAssigneePopup, setShowAssigneePopup] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  useEffect(() => { setHighlighted(false); hideLocalTooltip(); }, [isInChain]);
  useEffect(() => { if (forceResetHover) { setHighlighted(false); hideLocalTooltip(); } }, [forceResetHover]);

  useEffect(() => {
    const updateLocalState = () => setIsThisDropdownOpen(isGlobalDropdownOpen(id));
    updateLocalState();
    return subscribeToDropdownChanges(updateLocalState);
  }, [id]);

  useEffect(() => { if (isThisDropdownOpen) { setHighlighted(false); hideLocalTooltip(); } }, [isThisDropdownOpen]);
  useEffect(() => () => { setHighlighted(false); hideLocalTooltip(); }, []);

  useEffect(() => {
    if (!tooltip.visible) return;
    const handleGlobalMove = (e) => {
      const rect = nodeRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        hideLocalTooltip();
        setHighlighted(false);
      }
    };
    document.addEventListener("mousemove", handleGlobalMove);
    return () => document.removeEventListener("mousemove", handleGlobalMove);
  }, [tooltip.visible]);

  const isCritical = showCriticalPath && totalFloat === 0;

  const ellipseStroke = useMemo(() => {
    if (highlighted && isInChain) return "#3B82F6";
    if (highlighted && isCritical) return "#EF4444";
    if (isInChain) return "#1A73E8";
    if (isCritical) return "#CC0000";
    return highlighted ? "#4285F4" : "#1b1b17";
  }, [highlighted, isInChain, isCritical]);

  // ─── assignee ────────────────────────────────────────────────────────────
  const assigneeList = Array.isArray(d)
    ? d.filter((n) => n && n.trim())
    : d && d.trim() ? [d] : [];
  const hasAssignee = assigneeList.length > 0;
  const assigneeColor = hasAssignee ? "#075985" : "#B0B0B0";
  const assigneeInitials = hasAssignee ? getInitials(assigneeList[0]) : "";
  const isMultipleAssignees = assigneeList.length > 1;

  // ─── maturity ────────────────────────────────────────────────────────────
  const isTeamProj = id && (id.startsWith("uuid:") || id.includes("-"));

  const getMaturityDisplay = (m) => {
    if (!m) return "No Status";
    const s = String(m).toLowerCase().trim();
    if (isTeamProj) {
      if (s === "done" || s === "complete" || s === "completed") return "Completed";
      if (s === "open") return "To Do";
      if (s === "inprogress") return "In Work";
      return m;
    }
    if (s === "late") return "Late";
    if (s === "delay" || s === "delayed") return "Delayed";
    if (s === "active") return "In Work";
    if (s === "create") return "Draft";
    if (s === "assign") return "To Do";
    if (s.includes("to do") || s.includes("todo")) return "To Do";
    if (s.includes("in work") || s.includes("in-work") || s === "inwork") return "In Work";
    if (s.includes("draft")) return "Draft";
    if (s.includes("in progress") || s.includes("inprogress")) return "In Work";
    if (s.includes("review") || s.includes("in review")) return "In Review";
    if (s.includes("complete") || s.includes("completed") || s.includes("done")) return "Completed";
    if (s.includes("release") || s.includes("released")) return "Released";
    if (s.includes("late") || s.includes("delayed")) return "Delayed";
    return m.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  };

  const getMaturityColor = (m) => {
    if (!m) return "rgb(110,110,110)";
    const s = String(m).toLowerCase().trim();

    if (s === "active" || s.includes("in work") || s.includes("in-work") || s.includes("inwork") || s.includes("in progress") || s === "inprogress")
      return showWarningIcon ? "rgba(150,59,59,1)" : "#1A73E8";
    if (s === "create" || s.includes("draft")) return "rgb(120,120,120)";
    if (s === "assign" || s.includes("to do") || s.includes("todo") || s === "open") return "rgb(110,110,110)";
    if (s.includes("review")) return "rgb(70,65,125)";
    if (s.includes("complete") || s.includes("done")) return "rgba(11,139,21,1)";
    if (s.includes("release")) return "rgba(37,165,224,1)";
    if (s.includes("late") || s.includes("delay")) return "rgba(150,59,59,1)";
    return "rgb(110,110,110)";
  };

  const displayMaturity = getMaturityDisplay(maturity);
  const maturityColor = getMaturityColor(maturity);

  // ─── subtitle icon ────────────────────────────────────────────────────────
  const subtitleIconMap = {
    "Pending Approval": <HourglassEmptyIcon style={{ fontSize: ICON_SIZE, color: "#F28C38" }} />,
    Phase: <AutorenewIcon style={{ fontSize: ICON_SIZE, color: "#234A96" }} />,
    Milestone: <FlagIcon style={{ fontSize: ICON_SIZE, color: "#2E7D32" }} />,
    Completed: <CheckCircleIcon style={{ fontSize: ICON_SIZE, color: "#2E7D32" }} />,
    Task: <AssignmentIcon style={{ fontSize: ICON_SIZE, color: "#707070" }} />,
    Late: <WarningIcon style={{ fontSize: ICON_SIZE, color: "#D9534F" }} />,
    Delayed: <WarningIcon style={{ fontSize: ICON_SIZE, color: "#D9534F" }} />,
  };
  const subtitleIcon = subtitleIconMap[subtitle] || <HourglassEmptyIcon style={{ fontSize: ICON_SIZE, color: "#707070" }} />;

  // ─── layout constants ─────────────────────────────────────────────────────
  const MATURITY_W = 70;
  const ASSIGNEE_R = 13;
  // Right-side elements anchored from right edge
  const MATURITY_X = width - RIGHT_PAD - MATURITY_W + 12;
  const ASSIGNEE_CX = MATURITY_X - 6 - ASSIGNEE_R;
  const L = LEFT;
  const CHEVRON_W = 20;
  // Chevron sits at the right edge of the pill, symmetrical with LEFT
  const CHEVRON_X = width - RIGHT_PAD - CHEVRON_W + 20;

  const openMenu = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    hideLocalTooltip();
    openGlobalDropdown(id);
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 150 });
    }
  }, [id]);
  const closeMenu = useCallback(() => closeGlobalDropdown(id), [id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) closeMenu();
    };
    if (isThisDropdownOpen) document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isThisDropdownOpen, closeMenu]);

  // ─── tooltip builder ──────────────────────────────────────────────────────
  const buildTooltip = () => {
    const displayAssignee = hasAssignee ? assigneeList.join(", ") : "No Assignee";
    const tooltipString =
      `Title: ${title}\nType: ${subtitle}\nEstimated Start Date: ${startDate || "—"}\nDue Date: ${dueDate || "—"}\nAssignee: ${displayAssignee}\nMaturity: ${displayMaturity || "—"}`;
    const tooltipNode = (
      <>
        <strong>Title:</strong> {title}<br />
        <strong>Type:</strong> {subtitle}<br />
        <strong>Estimated Start Date:</strong> {startDate || "—"}<br />
        <strong>Due Date:</strong> {dueDate || "—"}<br />
        <strong>{isMultipleAssignees ? "Assignees:" : "Assignee:"}</strong> {hasAssignee ? assigneeList.join(", ") : "No Assignee"}<br />
        <strong>Maturity:</strong> {displayMaturity || "—"}<br />
        {delayDays > 0 && <><strong style={{ color: "#D9534F" }}>Delayed By:</strong> {delayDays} Days<br /></>}
        {maturityDescription && <><strong>Maturity Description:</strong> {maturityDescription}<br /></>}
        {description && <><strong>Description:</strong> {description}</>}
      </>
    );
    showLocalTooltip(tooltipString, tooltipNode);
  };

  return (
    <g
      ref={nodeRef}
      transform={`translate(${x}, ${y})`}
      opacity={opacity}
      onMouseDown={(e) => onMouseDown && onMouseDown(e)}
      onMouseEnter={() => { if (!isInChain) setHighlighted(true); buildTooltip(); }}
      onMouseMove={() => { if (!tooltip.visible) { if (!isInChain) setHighlighted(true); buildTooltip(); } }}
      onMouseLeave={() => { setHighlighted(false); hideLocalTooltip(); }}
      style={{ cursor: dragNode === id ? "grabbing" : "grab", userSelect: "none" }}
    >
      {/* ── Main pill ── */}
      <rect
        x={0} y={0} width={width} height={height}
        rx={height / 2} ry={height / 2}
        fill={nodeColor || "#EBF3FB"}
        stroke={ellipseStroke}
        strokeWidth={isInChain || isCritical ? 3 : 1.5}
        filter={
          isInChain ? "url(#chainNodeElevation)"
            : isCritical && highlighted ? "url(#criticalNodeGlow)"
              : highlighted ? "url(#nodeHover)"
                : "none"
        }
        style={{ transition: highlighted && !isInChain ? "filter 0.3s ease, stroke-width 0.3s ease" : "none" }}
      />

      {/* Loading Indicator Overlay */}
      {loading && (
        <foreignObject x={0} y={0} width={width} height={height}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "6px",
              pointerEvents: "none",
              clipPath: `ellipse(${width / 2}px ${height / 2}px at ${width / 2}px ${height / 2}px)`
            }}
          >
            <div className="node-spinner"></div>
            <style>{`
              .node-spinner {
                width: 20px;
                height: 20px;
                border: 2px solid #234A96;
                border-top: 2px solid transparent;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </foreignObject>
      )}

      {/* ══ ROW 1: Title — left-aligned, same L as all rows ════════════════ */}
      <text
        x={L}
        y={ROW1_Y + 16}
        fontSize="13"
        fontWeight="600"
        fill="#234A96"
        fontFamily="Arial, sans-serif"
      >
        {title.length > 25 ? title.substring(0, 22) + "..." : title}
      </text>

      {/* + / − expand button — sits ON the pill border at top-right */}
      {hasChildren && (() => {
        const btnR = 9;
        const arcR = height / 2;
        const angle = Math.PI / 4;
        const btnX = (width - arcR) + arcR * Math.cos(angle);
        const btnY = arcR - arcR * Math.sin(angle);
        return (
          <g
            transform={`translate(${btnX}, ${btnY})`}
            onClick={(e) => { e.stopPropagation(); if (onDoubleClick) onDoubleClick(); }}
            style={{ cursor: "pointer" }}
          >
            <circle cx={0} cy={0} r={btnR} fill="white" stroke="#666" strokeWidth={1} />
            <foreignObject x={-8} y={-8} width={16} height={16}>
              {isExpanded
                ? <RemoveIcon style={{ fontSize: 14, color: "#555", width: "14px", height: "14px" }} />
                : <AddIcon style={{ fontSize: 14, color: "#555", width: "14px", height: "14px" }} />
              }
            </foreignObject>
          </g>
        );
      })()}

      {/* ══ ROW 2: icon → subtitle text → chevron at right ═════════════════ */}
      {/* Icon */}
      <foreignObject x={L} y={ROW2_Y} width={ICON_SIZE} height={ICON_SIZE}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          {subtitleIcon}
        </div>
      </foreignObject>

      {/* Subtitle text — from after icon up to before chevron/deliverables */}
      <foreignObject x={L + ICON_SIZE + 4} y={ROW2_Y} width={CHEVRON_X - (L + ICON_SIZE + 4) - 70} height={ICON_SIZE + 2}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          fontSize: "12px", color: "#0f0e0e", lineHeight: `${ICON_SIZE + 2}px`,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          textAlign: "left",
        }}>
          {subtitle}
        </div>
      </foreignObject>

      {/* Chevron — moved to row 2, right-aligned */}
      <g
        ref={dropdownRef}
        transform={`translate(${CHEVRON_X}, ${ROW2_Y - 3})`}
        onClick={(e) => { e.stopPropagation(); isThisDropdownOpen ? closeMenu() : openMenu(e); }}
        style={{ cursor: "pointer" }}
      >
        <foreignObject width={CHEVRON_W} height={20}>
          <ArrowDropDownIcon style={{ fontSize: 20, color: "#5A5A5A" }} />
        </foreignObject>
      </g>

      {/* Deliverables dots — right-aligned in row 2, before chevron */}
      <DeliverablesDots
        taskId={objectId || id}
        x={CHEVRON_X - 65}
        y={ROW2_Y + (ICON_SIZE / 2) + 2}
        setTooltip={setTooltip}
        zoomRatio={zoomRatio}
      />

      {/* ══ ROW 3: Due date | assignee circle | maturity pill ══════════════ */}
      {/* Due date label */}
      <text x={L} y={ROW3_Y + 8} fontSize="8" fill="#999" fontWeight="600" letterSpacing="0.5" fontFamily="Arial, sans-serif">DUE DATE</text>

      {/* Due date value */}
      <text x={L} y={ROW3_Y + 23} fontSize="13" fill="#333" fontFamily="Arial, sans-serif">
        {dueDate}
      </text>

      {/* Assignee circle */}
      <g
        transform={`translate(${ASSIGNEE_CX}, ${ROW3_Y + 14})`}
        onMouseEnter={() => {
          const displayAssignee = hasAssignee ? assigneeList.join(", ") : "No Assignee";
          showLocalTooltip(`Assignee: ${displayAssignee}`, <><strong>Assignee:</strong> {displayAssignee}</>);
        }}
        onMouseLeave={hideLocalTooltip}
        style={{ cursor: "default" }}
      >
        {isMultipleAssignees ? (
          <foreignObject x={-13} y={-13} width={26} height={26}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{
              width: 26, height: 26, borderRadius: "50%", backgroundColor: "#075985",
              display: "flex", justifyContent: "center", alignItems: "center",
            }}>
              <GroupIcon style={{ fontSize: 18, color: "#fff" }} />
            </div>
          </foreignObject>
        ) : (
          <>
            <circle r={ASSIGNEE_R} fill={assigneeColor} />
            {assigneeInitials && (
              <text x="0" y="4" fontSize="13" textAnchor="middle" fill="white" fontWeight="600" fontFamily="Arial, sans-serif">
                {assigneeInitials}
              </text>
            )}
          </>
        )}
      </g>

      {/* Maturity pill */}
      <g
        transform={`translate(${MATURITY_X}, ${ROW3_Y})`}
        onMouseEnter={() => {
          const tooltipNode = (
            <>
              <strong>Maturity:</strong> {displayMaturity || "—"}<br />
              {maturityDescription && <><strong>Description:</strong> {maturityDescription}<br /></>}
            </>
          );
          showLocalTooltip(`Maturity: ${displayMaturity || "—"}\n${maturityDescription || ""}`, tooltipNode);
        }}
        onMouseLeave={hideLocalTooltip}
      >
        <rect
          width={MATURITY_W} height={28} rx={10}
          fill={maturityColor}
          stroke={maturityColor === "#FFFFFF" ? "#C8C6B8" : "none"}
          strokeWidth={1}
        />
        <text
          x={MATURITY_W / 2}
          y={28 / 2 + 1}
          fontSize="13"
          fontWeight="600"
          fill={maturityColor === "#FFFFFF" ? "#333333" : "white"}
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {displayMaturity}
        </text>
      </g>

      {/* ══ Dropdown menu ══════════════════════════════════════════════════ */}
      {isThisDropdownOpen && dropdownRef.current && createPortal(
        (() => {
          const taskId = objectId || id;
          return (
            <div
              style={{
                position: "fixed", top: menuPos.top, left: menuPos.left,
                width: 150, background: "white", border: "1px solid #C8C6B8",
                borderRadius: "4px", fontSize: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                fontFamily: "Arial", padding: "4px", zIndex: 999999999, pointerEvents: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Open */}
              <div
                style={{ padding: 8, display: "flex", alignItems: "center", cursor: "pointer", backgroundColor: hoverIndex === 0 ? "#bbb8b88a" : "transparent", borderRadius: 4, borderBottom: "1px solid #ccc" }}
                onMouseEnter={() => setHoverIndex(0)} onMouseLeave={() => setHoverIndex(null)}
                onClick={(e) => { e.stopPropagation(); window.open(`${ENOVIA_BASE_URL}/common/emxNavigator.jsp?isPopup=false&objectId=${taskId}`, "_blank"); }}
              >
                <CheckCircleOutlineIcon style={{ fontSize: 16, marginRight: 6 }} /> Open
              </div>

              {/* Add Deliverables */}
              <div
                style={{ padding: 8, display: "flex", alignItems: "center", cursor: "pointer", backgroundColor: hoverIndex === 10 ? "#bbb8b88a" : "transparent", borderRadius: 4, borderBottom: "1px solid #ccc" }}
                onMouseEnter={() => setHoverIndex(10)} onMouseLeave={() => setHoverIndex(null)}
                onClick={(e) => { e.stopPropagation(); closeMenu(); if (onAddDeliverables) onAddDeliverables(taskId, title); }}
              >
                <AttachFileIcon style={{ fontSize: 16, marginRight: 6, color: "#1A73E8" }} /> Add Deliverables
              </div>

              {/* Chain */}
              {(hasChildren || isInChain) && (
                <div
                  style={{
                    padding: 8,
                    display: "flex",
                    alignItems: "center",
                    cursor: timelineMode ? "not-allowed" : "pointer",
                    backgroundColor: hoverIndex === 3 && !timelineMode ? "#bbb8b88a" : "transparent",
                    borderRadius: 4,
                    borderBottom: "1px solid #ccc",
                    opacity: timelineMode ? 0.5 : 1,
                  }}
                  onMouseEnter={() => !timelineMode && setHoverIndex(3)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={(e) => {
                    if (timelineMode) return;
                    e.stopPropagation();
                    e.preventDefault();
                    if (onHighlightChain) onHighlightChain(id, isInChain);
                    closeMenu();
                  }}
                  title={timelineMode ? "Layered view is disabled in Timeline Mode" : ""}
                >
                  <LayersIcon style={{ fontSize: 16, marginRight: 6, color: isInChain ? "#1A73E8" : "#234A96" }} />
                  {isInChain ? "Exit Layered View" : "Show Layered View"}
                </div>
              )}

              {/* Change Assignee */}
              {(() => {
                const isCompleted = displayMaturity === "Completed" || displayMaturity === "Released";
                return (
                  <div
                    style={{
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      cursor: isCompleted ? "not-allowed" : "pointer",
                      backgroundColor: hoverIndex === 1 && !isCompleted ? "#bbb8b88a" : "transparent",
                      borderRadius: 4,
                      borderBottom: "1px solid #ccc",
                      opacity: isCompleted ? 0.5 : 1,
                    }}
                    onMouseEnter={() => !isCompleted && setHoverIndex(1)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onClick={(e) => {
                      if (isCompleted) return;
                      e.stopPropagation();
                      closeMenu();
                      setShowAssigneePopup(true);
                    }}
                  >
                    <PersonIcon style={{ fontSize: 16, marginRight: 6 }} /> Change Assignee
                  </div>
                );
              })()}

              {/* Promote */}
              {(() => {
                const isPromoteDisabled = displayMaturity === "Completed" || displayMaturity === "Released" || subtitle === "Phase";
                return (
                  <div
                    style={{
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      cursor: isPromoteDisabled ? "not-allowed" : "pointer",
                      backgroundColor: hoverIndex === 2 && !isPromoteDisabled ? "#bbb8b88a" : "transparent",
                      borderRadius: 4,
                      opacity: isPromoteDisabled ? 0.5 : 1,
                    }}
                    onMouseEnter={() => !isPromoteDisabled && setHoverIndex(2)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onClick={(e) => {
                      if (isPromoteDisabled) return;
                      e.stopPropagation();
                      const taskIdToPromote = objectId || id;
                      if (!taskIdToPromote) return;
                      const getNextState = (m) => {
                        const s = String(m || "").toLowerCase().trim();
                        if (isTeamProj) {
                          if (s === "draft") return "Open";
                          if (s === "open") return "InProgress";
                          if (s === "inprogress") return "Done";
                          return "Open";
                        }
                        if (s === "create" || s.includes("draft")) return "Assign";
                        if (s === "assign" || s.includes("to do") || s.includes("todo")) return "Active";
                        if (s === "active" || s.includes("in work") || s.includes("progress")) return "Review";
                        if (s.includes("review")) return "Complete";
                        return "Active";
                      };
                      const nextState = getNextState(maturity);
                      closeMenu();
                      setHoverIndex(null);
                      setTooltip((t) => ({ ...t, visible: false }));

                      if (onPromoteNode) {
                        onPromoteNode(id, taskIdToPromote, nextState, maturity);
                      } else {
                        if (onUpdateNode) onUpdateNode(id, { maturity: nextState });
                        Service.EnoviaService()
                          .promoteTask(taskIdToPromote, nextState)
                          .then(() => { showSuccessToast("Task promoted successfully!"); if (!onUpdateNode && onRefresh) onRefresh(); })
                          .catch(() => { showErrorToast("Failed to promote task. Reverting state..."); if (onUpdateNode) onUpdateNode(id, { maturity }); });
                      }
                    }}
                  >
                    <ArrowUpwardIcon style={{ fontSize: 16, marginRight: 6 }} /> Promote
                  </div>
                );
              })()}
            </div>
          );
        })(),
        document.body
      )}

      {/* ══ Tooltip ════════════════════════════════════════════════════════ */}
      {!isThisDropdownOpen && !showAssigneePopup && tooltip.visible && createPortal(
        (() => {
          const nodeRect = nodeRef.current?.getBoundingClientRect() || { top: 0, left: 0, bottom: 0, right: 0 };
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          const MARGIN = 8;
          const spaceBelow = vh - nodeRect.bottom;
          const tooltipTop = spaceBelow < tooltip.height + MARGIN
            ? Math.max(MARGIN, nodeRect.top - tooltip.height - 4)
            : nodeRect.bottom + 4;
          const rawLeft = nodeRect.right - tooltip.width;
          const tooltipLeft = Math.max(MARGIN, Math.min(rawLeft, vw - tooltip.width - MARGIN));
          return (
            <div style={{
              position: "fixed", top: tooltipTop, left: tooltipLeft,
              minWidth: tooltip.width, background: "white",
              border: "1px solid #363a3fff", borderRadius: "8px",
              padding: "8px", fontSize: "12px", color: "#202124",
              whiteSpace: "pre-line", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              pointerEvents: "none", zIndex: 999999999, lineHeight: "1.6",
            }}>
              {tooltip.textNode}
            </div>
          );
        })(),
        document.body
      )}

      {/* ══ Assignee popup ═════════════════════════════════════════════════ */}
      {showAssigneePopup && (
        <AssigneePopup
          taskId={objectId || id}
          projectId={projectId}
          taskName={title}
          onClose={() => setShowAssigneePopup(false)}
          onUpdate={(newAssignees) => { if (onUpdateNode) onUpdateNode(id, { assignees: newAssignees }); }}
        />
      )}
    </g>
  );
};

export default EllipseNode;